// src/core/score/dealScoreConfig.ts
// Grenzwerte und Gewichte je Analyzer-Typ, als Konfiguration statt im Code verstreut
// (siehe dealScore.ts). Werte aus der Deal-Score-Spezifikation v3 (Stand 09.10.2026),
// an 8 echten Objekten kalibriert (tests/deal-score.test.mjs) -- alle 8 treffen Score
// und Urteil exakt.
//
// Nur ETW ist aktuell scharf. Die anderen 4 Analyzer-Typen sind bewusst noch nicht
// konfiguriert (kommen nach Freigabe der ETW-Kalibrierung in Folge-Schritten, siehe
// PHASE-0-BEFUND.md) -- Grenzwerte je Typ anpassbar (z. B. Gewerbe hoehere
// Renditeanforderung), aber noch nicht festgelegt.

import type { DealScoreConfig } from "./dealScore.js";

export const DEAL_SCORE_CONFIG_VERSION = 1;

export const ETW_SCORE_CONFIG: DealScoreConfig = {
  version: DEAL_SCORE_CONFIG_VERSION,
  rendite: {
    // Bruttomietrendite Deal minus Marktrendite der PLZ, in Prozentpunkten.
    anchors: [[-2, 0], [0, 12], [1.5, 20]],
    weight: 20,
  },
  lage: {
    // Perzentil des Kaufpreisniveaus k ueber alle PLZ (hoch = teure/gefragte Lage = hohe Qualitaet).
    anchors: [[10, 0], [90, 20]],
    weight: 20,
    obergrenzePctThreshold: 20,
    obergrenzeCap: 69,
    obergrenzeHinweis:
      "Die Zahlen sind stark, aber das niedrige Preisniveau zeigt eine schwache Nachfrage am Standort. " +
      "Das erhöht das Risiko bei Leerstand und Wiederverkauf. Deshalb bewerten wir den Deal höchstens als solide.",
    ohnePlzAnnahmeR: 0.055,
    ohnePlzPunkteAnteil: 12,
    ohnePlzHinweis: "Lage nicht bewertet. Gib die PLZ an, damit wir sie einbeziehen.",
    plzUnbekanntHinweis: "Für kleinere Gemeinden haben wir keine Marktdaten. Vermietung und Wiederverkauf sind dort schwerer einzuschätzen.",
  },
  noi: {
    // NOI-Rendite auf Gesamtinvestition (inkl. Nebenkosten), in Prozent.
    anchors: [[2, 0], [5, 10]],
    weight: 10,
  },
  cashflow: {
    // Cashflow nach Zins+Tilgung relativ zur (rohen) Jahreskaltmiete, in Prozent.
    anchors: [[-30, 0], [0, 15], [15, 25]],
    weight: 25,
  },
  dscr: {
    anchors: [[0.9, 0], [1.0, 6], [1.3, 15]],
    weight: 15,
  },
  stress: {
    // Stress-DSCR bei Zins +1pp und Miete -10%.
    anchors: [[0.8, 0], [1.0, 10]],
    weight: 10,
  },
  urteil: { gut: 75, solide: 60, kritisch: 45 },
};

/**
 * MFH/EFH/Gewerbe/Gemischt (mixeduse) teilen sich alle Anker mit ETW, bis auf die DSCR-Anker bei
 * Gewerbe (hoehere Anforderung) und den Rendite-Zuschlag/die Bewirtschaftungs-Pauschale (siehe
 * RENDITE_ZUSCHLAG_PP / BEWIRTSCHAFTUNG_PAUSCHALE_PCT unten) -- kalibriert an 14 echten Objekten
 * (Stand 11.10.2026, siehe PHASE-ALLE-TYPEN-BEFUND.md §7), 14/14 treffen Score ±1 und Urteil exakt.
 */
const BASIS_DSCR_ANCHORS = [[0.9, 0], [1.0, 6], [1.3, 15]] as const;

export const MFH_SCORE_CONFIG: DealScoreConfig = {
  ...ETW_SCORE_CONFIG,
  dscr: { anchors: BASIS_DSCR_ANCHORS, weight: 15 },
};

export const GEMISCHT_SCORE_CONFIG: DealScoreConfig = {
  ...ETW_SCORE_CONFIG,
  dscr: { anchors: BASIS_DSCR_ANCHORS, weight: 15 },
};

export const GEWERBE_SCORE_CONFIG: DealScoreConfig = {
  ...ETW_SCORE_CONFIG,
  dscr: { anchors: [[1.0, 0], [1.1, 6], [1.4, 15]], weight: 15 },
};

export const EFH_SCORE_CONFIG: DealScoreConfig = {
  ...ETW_SCORE_CONFIG,
  dscr: { anchors: BASIS_DSCR_ANCHORS, weight: 15 },
};

/** Objektart-Discriminator nur fuer diese beiden Tabellen (vermeidet Importe aus Hook-Dateien im Core-Layer). */
export type ScoreTypeId = "mfh" | "efh" | "gewerbe" | "mixeduse";

/** Zuschlag in Prozentpunkten auf r (PLZ-Wert UND Ersatzwert 5,5%), je Objektart. ETW: 0. */
export const RENDITE_ZUSCHLAG_PP: Record<ScoreTypeId, number> = {
  mfh: 0.5,
  mixeduse: 1.0,
  gewerbe: 2.0,
  efh: -0.5,
};

/** Flat Bewirtschaftungskosten-Pauschale (Anteil der Kaltmiete), wenn kein Override angegeben ist. ETW nutzt stattdessen das Hausgeld+Mietausfall-Modell. */
export const BEWIRTSCHAFTUNG_PAUSCHALE_PCT: Record<ScoreTypeId, number> = {
  mfh: 0.20,
  mixeduse: 0.22,
  gewerbe: 0.20,
  efh: 0.15,
};

/** Score-Config je Objektart, fuer generische Call-Sites (z. B. /check-Adapter). */
export const SCORE_CONFIG_BY_TYPE: Record<ScoreTypeId, DealScoreConfig> = {
  mfh: MFH_SCORE_CONFIG,
  mixeduse: GEMISCHT_SCORE_CONFIG,
  gewerbe: GEWERBE_SCORE_CONFIG,
  efh: EFH_SCORE_CONFIG,
};

/** Zusaetzlicher, nicht gepunkteter Hinweis bei Zins +2pp (eigener Stresstest, separat vom gewichteten +1pp-Test). */
export function stress2ppHinweis(stressDscrAt2pp: number | null): string | null {
  if (stressDscrAt2pp === null) return null;
  if (stressDscrAt2pp < 1.0) {
    return "Bei einem Zinsanstieg um 2 Prozentpunkte würde die Schuldendeckung (DSCR) unter 1,0 fallen — die Rate wäre dann nicht mehr allein aus dem Objekt gedeckt.";
  }
  return null;
}
